import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShoppingCartIcon as ShoppingCart, WarningIcon as Warning, ScalesIcon as Scales, ClockIcon as Clock, GlobeIcon as Globe, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { getReferralCode } from "@/components/ReferralCapture";
import BuyerConsentGate, { useBuyerConsent } from "@/components/BuyerConsentGate";

/** Days from DOB to today (UTC). Mirrors backend `puppy_age_guard`. */
function ageInDays(dobValue) {
  if (!dobValue) return null;
  const s = String(dobValue).slice(0, 10);
  const parsed = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  const t = new Date();
  const utcToday = Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
  return Math.floor((utcToday - parsed.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Buy-now button for marketplace listings.
 * Verifies the seller has a connected Stripe account and the listing has
 * an asking_price > 0 before kicking off escrow checkout.
 */
export default function BuyNowButton({ listing, currentUserId, variant = "card" }) {
  const [busy, setBusy] = useState(false);
  const [canReceive, setCanReceive] = useState(null);
  const [rightsGate, setRightsGate] = useState(null); // { version, message } when ack required
  const consent = useBuyerConsent(listing);

  useEffect(() => {
    if (!listing?.created_by) return;
    api
      .get(`/connect/by-user/${listing.created_by}/can-receive`)
      .then((r) => setCanReceive(Boolean(r.data?.onboarded)))
      .catch(() => setCanReceive(false));
  }, [listing?.created_by]);

  // Hide if seller can't receive payouts, no asking price, or own dog.
  if (!listing?.id || !currentUserId) return null;
  if (currentUserId === listing.created_by) return null;
  if (!listing.asking_price || listing.asking_price < 1) return null;
  if (canReceive === null) return null;
  const isInternationalSeller = !!listing.seller_country && listing.seller_country !== "US";
  if (canReceive === false) {
    if (isInternationalSeller) {
      // International contact-to-buy listing: payouts intentionally
      // unavailable (escrow is US-only) — not a seller setup problem.
      return (
        <div className="space-y-1.5" data-testid={`buy-now-international-${listing.id}`}>
          <div className="text-xs text-[#3F3F3F] bg-gold/5 border border-gold/30 rounded-lg p-2.5 flex items-start gap-1.5 leading-relaxed">
            <Globe size={13} weight="duotone" className="text-[#8A6A00] mt-0.5 flex-shrink-0" />
            <span>
              <strong>International breeder — contact to buy.</strong>{" "}
              Use <em>Show interest</em> to message the breeder; payment and
              transfer are arranged directly between you. Armstrong K9 Registry
              escrow isn&rsquo;t available on international listings.
            </span>
          </div>
          <div
            className="text-xs text-[#3F3F3F] bg-[#FDF7F0] border border-[#E8D5B5] rounded-lg p-2.5 leading-relaxed"
            data-testid={`intl-buyer-safety-${listing.id}`}
          >
            <div className="flex items-start gap-1.5">
              <ShieldCheck size={13} weight="duotone" className="text-[#8A6A00] mt-0.5 flex-shrink-0" />
              <div>
                <strong>Protect yourself before paying anyone directly:</strong>
                <ul className="list-disc ml-4 mt-1 space-y-0.5">
                  <li>Ask for a <strong>live video call</strong> showing the puppy and its dam before sending any money.</li>
                  <li>Request references, health records, and pedigree documents — verify them independently.</li>
                  <li><strong>Never pay by wire transfer, gift card, or crypto.</strong> Use a payment method with buyer protection.</li>
                  <li>Be wary of pressure to pay a deposit fast, prices far below market, or excuses that block a video call.</li>
                  <li>Armstrong K9 Registry cannot recover funds sent directly to a breeder on contact-to-buy listings.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }
    // Surface a soft hint to the seller (own card scenario excluded above) so
    // browsers know why no Buy button appears for high-asking listings.
    return (
      <div
        className="text-xs text-[#6B6B6B] bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-2 flex items-center gap-1.5"
        data-testid={`buy-now-pending-${listing.id}`}
      >
        <Warning size={12} className="text-yellow-600" />
        Breeder hasn't connected payouts yet — message them via Show Interest.
      </div>
    );
  }

  // Under-8-week pre-eligibility hold. Backend escrow Checkout
  // hard-blocks the actual money move (see services/puppy_age_guard.py)
  // but rendering a disabled Buy button + a "ready in N days" hint
  // here saves the buyer from a confusing 400 toast after they click.
  const puppyAgeDays = ageInDays(listing?.dob);
  const isUnder8wk = puppyAgeDays !== null && puppyAgeDays < 56;
  if (isUnder8wk) {
    const ready = 56 - puppyAgeDays;
    return (
      <div
        className="text-[11.5px] text-[#A4222A] bg-[#FFF4F4] border border-[#C1272D]/35 rounded-lg p-2.5 flex items-start gap-1.5 leading-relaxed"
        data-testid={`buy-now-under-8wk-${listing.id}`}
      >
        <Clock size={14} weight="duotone" className="text-[#C1272D] mt-0.5 flex-shrink-0" />
        <span>
          <strong>Eligible in {ready} day{ready === 1 ? "" : "s"}.</strong>{" "}
          U.S. state law requires puppies to be at least 8 weeks old before
          transfer. Use <em>Show interest</em> to start a conversation with the
          breeder now — the Buy button unlocks once the puppy reaches 8 weeks.
        </span>
      </div>
    );
  }

  const submitCheckout = async ({ ackBuyerRights = false, ackRefundPolicy = false } = {}) => {
    setBusy(true);
    try {
      const ref = getReferralCode();
      const r = await api.post("/escrow/checkout", {
        dog_id: listing.id,
        origin_url: webOrigin(),
        ref_code: ref || undefined,
        buyer_consent: {
          acknowledged: true,
          breed_warning: listing?.breed_warning || null,
          ack_buyer_rights: ackBuyerRights || undefined,
          ack_refund_policy: ackRefundPolicy || undefined,
        },
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Checkout did not return a URL");
        setBusy(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 412 &&
        detail?.code === "buyer_rights_ack_required"
      ) {
        setRightsGate({
          kind: "buyer_rights",
          version: detail.version,
          message: detail.message,
          rights_url: detail.rights_url || "/buyer-rights",
        });
        setBusy(false);
        return;
      }
      if (
        err?.response?.status === 412 &&
        detail?.code === "refund_policy_ack_required"
      ) {
        setRightsGate({
          kind: "refund_policy",
          version: "Path-1 tiers (2026-02-04)",
          message: detail.message,
          rights_url: detail.policy_url || "/refund-policy",
        });
        setBusy(false);
        return;
      }
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const buy = async () => {
    if (!consent.ok) {
      toast.info(consent.isRestricted
        ? "Acknowledge the restricted-breed notice below to continue."
        : "Please confirm the buyer checklist below to continue.");
      return;
    }
    await submitCheckout();
  };

  const acceptRightsAndRetry = async () => {
    const wasRefundPolicy = rightsGate?.kind === "refund_policy";
    setRightsGate(null);
    // Stack both acks — buyer reaches checkout once both are
    // satisfied. The two gates are independent server-side so
    // sending both is safe even if only one was pending.
    await submitCheckout({
      ackBuyerRights: !wasRefundPolicy || true,
      ackRefundPolicy: true,
    });
  };

  return (
    <>
      <Button
        onClick={buy}
        disabled={busy || !consent.ok}
        data-testid={`buy-now-btn-${listing.id}`}
        className={
          variant === "card"
            ? "w-full rounded-full bg-[#C9A227] hover:bg-[#0E0E0E] text-white"
            : "rounded-full bg-[#C9A227] hover:bg-[#0E0E0E] text-white"
        }
        size="sm"
      >
        <ShoppingCart size={14} className="mr-1.5" weight="bold" />
        {busy ? "Opening checkout…" : `Buy now · $${Number(listing.asking_price).toFixed(0)}`}
      </Button>
      <BuyerConsentGate
        listing={listing}
        actionLabel="buy"
        testId={`consent-gate-buy-${listing.id}`}
        onConsentChange={consent.refresh}
      />
      <AlertDialog open={!!rightsGate} onOpenChange={(o) => { if (!o) setRightsGate(null); }}>
        <AlertDialogContent data-testid="buyer-rights-gate-dialog" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-spectral text-2xl">
              <Scales size={20} weight="duotone" className="text-gold" />
              {rightsGate?.kind === "refund_policy"
                ? `Refund policy · ${rightsGate?.version}`
                : `Buyer's bill of rights · ${rightsGate?.version}`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13.5px] leading-relaxed text-[#3F3F3F]">
              {rightsGate?.message ||
                "Before any money moves, the Registry strongly encourages every buyer to read these rights."}
              <br /><br />
              <Link
                to={rightsGate?.rights_url || "/buyer-rights"}
                target="_blank"
                rel="noreferrer"
                className="underline text-crimson font-semibold"
                data-testid="buyer-rights-gate-read-link"
              >
                {rightsGate?.kind === "refund_policy"
                  ? "Open the full Refund Policy →"
                  : "Open the full Buyer's Bill of Rights →"}
              </Link>
              <br /><br />
              {rightsGate?.kind === "refund_policy" ? (
                <>
                  By continuing, you confirm you understand: <strong>(a)</strong>{" "}
                  Armstrong K9 Registry is a venue and does not adjudicate
                  disputes; <strong>(b)</strong> if you file a dispute and
                  don&apos;t reach mutual agreement within 30 days, the
                  platform mechanically refunds you in full (longer if your
                  seller publishes an extension); <strong>(c)</strong> if
                  your seller has declared a non-refundable deposit, that
                  portion is forfeit if you cancel before pickup; and{" "}
                  <strong>(d)</strong> Stripe chargebacks remain available
                  as a parallel recourse.
                </>
              ) : (
                <>
                  By continuing, you confirm you&apos;ve read these rights
                  and accept that Armstrong K9 Registry is a venue and not
                  the seller, breeder, or guarantor of any animal.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="buyer-rights-gate-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction
              onClick={acceptRightsAndRetry}
              data-testid="buyer-rights-gate-accept"
              className="bg-[#0E0E0E] hover:bg-[#C9A227] text-white"
            >
              {rightsGate?.kind === "refund_policy"
                ? "I've read the refund policy — continue"
                : "I've read my rights — continue to checkout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
